import React, { Component } from "react"
import dynamic from "next/dynamic"
import PropTypes from "prop-types"
import Head from "next/head"
import styled from "styled-components"
import { inject, observer } from "mobx-react"
import { REQUEST_STATUSES, PAYMENT_PROVIDERS } from "~/utilities/constants"
import { showCkoFlowDropin } from "~/stores/paymentFlowActions"
import Loading from "./Loading"
import DefaultPaymentMethod from "./DefaultPaymentMethod"
import DefaultAddress from "./UserAddress"
import UserAddress from "./UserAddress/Form"
import PaymentFlowContainer from "./dropins/cko/PaymentFlowContainer"

const StripeForm = dynamic(() => import("./dropins/stripe/StripeForm"))
const StripeACHForm = dynamic(() => import("./dropins/stripe/StripeACHForm"))
const BraintreeDropin = dynamic(() => import("./dropins/braintree"))
const ThreeDSecureModal = dynamic(() => import("./ThreeDSecureModal"))
const PaymentFlowInitializer = dynamic(() => import("./PaymentFlowInitializer"), { ssr: false })
const EvervaultCard = dynamic(() => import("./dropins/evervault"))

const Wrapper = styled.div`
  display: ${({ isLoading }) => (isLoading ? "none" : "flex")};
  flex-direction: column;
  gap: 1rem;
  height: ${({ isLoading }) => (isLoading ? 0 : "auto")};
  overflow: ${({ isLoading }) => (isLoading ? "hidden" : "visible")};
  transition: all 0.5s ease;
`

const FormToggle = styled.div`
  height: ${({ show }) => (show ? "auto" : 0)};
  overflow: ${({ show }) => (show ? "visible" : "hidden")};
  position: relative;
  transition: all 0.5s ease;
`

@inject((all) => ({
  user: all.store.auth.user,
  payment: all.store.payment,
  isLoading: all.store.payment.isLoading,
  collapsePaymentFormWhileLoading: all.store.payment.collapsePaymentFormWhileLoading,
  initialized: all.store.payment.initialized,
  clientToken: all.store.payment.clientToken,
  clientTokenStatus: all.store.payment.clientTokenStatus,
  isAddingNewPaymentMethod: all.store.payment.isAddingNewPaymentMethod,
  isChangingAddress: all.store.payment.isChangingAddress,
  isPayFlow: all.store.parkingFlow.flow.isPayFlow,
  isRobotPayFlow: all.store.parkingFlow.flow.isRobotPayFlow,
  isPayFlowFromMobile: all.store.parkingFlow.flow.isPayParkingFromMobile,
  isFightFlow: all.store.parkingFlow.flow.isFightFlow,
  isTrafficFlow: all.store.ui.isTrafficFlow,
  isParkingFlow: all.store.parkingFlow?.flow?.isFightFlow ||
    all.store.parkingFlow?.flow?.isPayFlow ||
    all.store.parkingFlow?.flow?.isRobotPayFlow,
  isACH: all.store.payment.isACH,
}))
@observer
class PaymentContainer extends Component {
  constructor(props) {
    super(props)
    this.state = { initialized: false }
  }

  componentDidMount = async () => {
    await this.props.payment.initialize({
      isTosRequired: this.props.isTosRequired,
      checkoutFunction: this.props.checkoutFunction,
      use3DS: this.props.use3DS,
      get3DSVerifyAmount: this.props.get3DSVerifyAmount,
    })
    this.setState({ initialized: true })
  }

  componentDidCatch(error, info) {
    console.error("error ----- did catch --->", error, info)
  }

  componentWillUnmount() {
    this.props.payment.teardown()
  }

  render() {
    const {
      initialized,
      clientTokenStatus,
      isChangingAddress,
      isLoading,
      collapsePaymentFormWhileLoading,
      simple,
      payment,
      isAddingNewPaymentMethod,
      isPayFlow,
      isRobotPayFlow,
      isPayFlowFromMobile,
      isFightFlow,
      isTrafficFlow,
      isParkingFlow,
      isDashboardPayment = false,
      isACH,
    } = this.props

    const showCkoFlow = showCkoFlowDropin(payment.paymentProcessor, {
      isFightFlow,
      isTrafficFlow,
      isPayFlow,
      isRobotPayFlow,
      isPayFlowFromMobile,
      isDashboardPayment,
    })

    let loaderMessage
    if (!initialized) {
      loaderMessage = "Loading..."
    }
    if (clientTokenStatus === REQUEST_STATUSES.loading) {
      loaderMessage = "Preparing Secure Payment Form..."
    }
    return (
      <>
        {!isACH && (
        <Head>
          <script
            id="stripe-js"
            src="https://js.stripe.com/basil/stripe.js"
            async
          />
        </Head>
)}

        {collapsePaymentFormWhileLoading && <Loading message={loaderMessage} />}

        <Wrapper isLoading={collapsePaymentFormWhileLoading}>
          {isACH ? (
            <StripeACHForm />
          ) : (
            <>
              {((this.state.initialized &&
                payment.paymentProcessor === PAYMENT_PROVIDERS.braintree) ||
                (payment.paymentProcessor === PAYMENT_PROVIDERS.evervault &&
                  process.env.ENABLE_BT_PAYMENTS === "true" &&
                  (isFightFlow || isTrafficFlow))) && (
                  <BraintreeDropin payment={payment} />
              )}

              {(payment.paymentProcessor === PAYMENT_PROVIDERS.stripe ||
                (payment.paymentProcessor === PAYMENT_PROVIDERS.evervault &&
                  process.env.ENABLE_STRIPE_PAYMENTS === "true" &&
                  (isFightFlow || isTrafficFlow))) && (
                  <>
                    <DefaultPaymentMethod />
                    <FormToggle show={isAddingNewPaymentMethod}>
                      <StripeForm />
                    </FormToggle>
                  </>
              )}

              {showCkoFlow && (
                <>
                  <PaymentFlowInitializer
                    isDashboardPayment={isDashboardPayment}
                    isTrafficFlow={isTrafficFlow}
                    isParkingFlow={isParkingFlow}
                  />
                  <PaymentFlowContainer
                  initialized={payment._flowSessionInitialized || payment.cko.isReady}
                  isDashboard={isDashboardPayment}
                  showBillingAddress={!isDashboardPayment}
                  />
                </>
              )}

              {!(
                simple ||
                payment.paymentProcessor === PAYMENT_PROVIDERS.cko ||
                payment.paymentProcessor === PAYMENT_PROVIDERS.evervault
              ) && (
                <>
                  <DefaultAddress />
                  <FormToggle show={isChangingAddress}>
                    <UserAddress />
                  </FormToggle>
                </>
              )}

              {payment.paymentProcessor === PAYMENT_PROVIDERS.evervault &&
                (isPayFlow || isRobotPayFlow || isPayFlowFromMobile) && (
                  <EvervaultCard payment={payment} />
                )}
            </>
          )}
        </Wrapper>

        {this.props.use3DS && <ThreeDSecureModal />}
      </>
    )
  }
}

PaymentContainer.propTypes = {
  checkoutFunction: PropTypes.func.isRequired,
  use3DS: PropTypes.bool,
  get3DSVerifyAmount: PropTypes.func,
  isDashboardPayment: PropTypes.bool,
}

PaymentContainer.defaultProps = {
  use3DS: false,
  get3DSVerifyAmount: () => {},
  isDashboardPayment: false,
}

export default PaymentContainer
